import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, tap, catchError, map, of, filter, throwError, EMPTY, take, switchMap, combineLatest, defer, finalize } from 'rxjs';
import { PaginationService } from '../pagination.service';
import { EntityType } from '../../objects/interface-library'; 
import { Party } from '../../objects/party';

@Injectable({
  providedIn: 'root'
})
export class PartyService {
  private http = inject(HttpClient);
  private paginationService = inject(PaginationService);

  private bdaSheetsApiKey = 'AIzaSyC-5gvjrj0vx82W30NwzAwXe6xLD2eOdVc';
  private googleSheetId = environment.fieldValuesGoogleSheetId;

  private httpOptions = {
    params: {
      'key': this.bdaSheetsApiKey,
      'majorDimension': 'COLUMNS'
    },
    headers: {
      'Content-Type': 'application/json'
    }
  };

  private allPartiesSubject = new BehaviorSubject<Party[]>([]);
  public allParties$ = this.allPartiesSubject.asObservable();
  private isCacheRefreshedSubject = new BehaviorSubject<boolean>(false);
  public isCacheRefreshed$ = this.isCacheRefreshedSubject.asObservable();

  /**
   * How many party crawls are in flight. `isCacheRefreshed$` flips true on the
   * FIRST page (the crawl emits progressively), so it can't tell a lookup whether
   * the list is complete. This can: 0 means the last crawl has finished.
   */
  private crawlsInFlightSubject = new BehaviorSubject<number>(0);

  constructor() {
    this.refreshAllPartiesRecords().subscribe({
      next: () => this.isCacheRefreshedSubject.next(true),
      error: (err) => {
        console.error('Initial party cache refresh failed.', err);
        this.isCacheRefreshedSubject.next(false);
      }
    });
  }

  // NOTE: You must also create this method. It is not included in the provided code.
  // public getPartyObject(partyIdOrName: string): Observable<Party | null> {
  //   return this.allParties$.pipe(
  //     map(parties => {
  //       const foundParty = parties.find(party => party.vendorNum === partyIdOrName || party.name === partyIdOrName);
  //       return foundParty || null;
  //     }),
  //     take(1)
  //   );
  // }
  

  /**
   * Performs a partial update on a specific party record by its ID.
   * This is more efficient than writing the entire object for small changes.
   * @param partyId The unique ID of the party to update.
   * @param updates A key-value object of the fields to update.
   * @returns An observable of the updated party data from the backend.
   */
  public updatePartyPartially(partyId: string, updates: { [key: string]: any }): Observable<Party> {
    if (!partyId) {
      console.error('PartyService: updatePartyPartially called without a partyId.');
      return throwError(() => new Error('Party ID is required for a partial update.'));
    }

    const payload = {
      updates: updates
    };

    const url = `${environment.URL_PARTIES_PARTIAL_UPDATE}${partyId}`;
    
    return this.http.put<Party>(url, payload).pipe(
      // tap(response => console.log('Successfully performed partial update on party:', response)),
      catchError(error => {
        console.error('An error occurred during the partial party update:', error);
        return throwError(() => new Error('Could not perform partial update on the party.'));
      })
    );
  }




  /**
   * Modern, reactive function to write a Party object to the database.
   * It replaces the old Promise-based approach with RxJS.
   * Automatically refreshes the party cache after a successful write.
   * @param party The Party object to write.
   * @returns An observable of the API response.
   */
  public writeParty(party: Party): Observable<any> {
    const jsonReadyBody = {
      'vendorNum': party.vendorNum,
      'vendorData': party,
    };

    const options = {
      headers : {
          'Content-Type' : 'application/json'
      }
    };

    return this.http.put(environment.partyWriteUrl, jsonReadyBody, options).pipe(
      tap(() => {
        // Show it NOW. The refresh below re-scans the whole table (~4,500 rows /
        // ~45 sequential pages for cbhi), so waiting on it means the party is
        // missing from lists and pickers for a long time — and if the scan hasn't
        // caught up yet, for the rest of the session.
        this.upsertPartyInCache(party);
        // Still refresh, to reconcile with the server's version of the record.
        this.refreshAllPartiesRecords().subscribe();
      }),
      catchError(error => {
        console.error('An error occurred while writing the party:', error);
        return throwError(() => new Error('Could not write party to the database.'));
      })
    );
  }

  /**
   * Modern, reactive function to save a new simplified party.
   * It now uses the local writeParty function and refreshes the cache.
   * @param partyName The name of the new party.
   * @returns An observable of the new party object from the database.
   */
  public saveNewPartySimplified(partyName: string | null): Observable<Party> {
    if (!partyName) {
      console.warn('Cannot save a party with a null name. Bailing out.');
      return of({} as Party); // Return an empty Party object to satisfy the type
    }

    const newPartyData: Party = { name: partyName } as Party;
    
    return this.writeParty(newPartyData).pipe(
      tap(() => {
        this.refreshAllPartiesRecords().subscribe();
      }),
      catchError(error => {
        console.error('Failed to save new party:', error);
        return throwError(() => new Error('Could not save new party.'));
      })
    );
  }

/**
 * Gets a fully-hydrated Party object by its ID.
 * @param partyId The ID of the Party to retrieve.
 * @returns An observable of the fully-hydrated Party or null if not found.
 */
public getPartyObjectFullyHydrated(partyId: string): Observable<Party | null> {

  const propertyMap = {
    'relatedBaas': EntityType.Baa,
    'relatedCois': EntityType.Coi,
    'coveredEntity': EntityType.Party,
    'businessAssociateName': EntityType.Party,
    'ceCoordinator': EntityType.Person,
    'baCoordinator': EntityType.Person,
    'personnel': EntityType.Person,
    'contracts': EntityType.Contract,
    'people': EntityType.Person
  };
  
  // getPartyObject waits for the party to load (or for the crawl to finish).
  // This used to wait on isCacheRefreshed$, which turns true after the FIRST page
  // of ~45 at cbhi scale — so most parties opened as an empty profile.
  return this.getPartyObject(partyId).pipe(
    switchMap(party => {
      if (!party) {
        return of(null);
      }
      // PUNTED: The hydration logic has been commented out.
      // We will add this back in when we are ready to deal with the connections.
      // return this.connectionsService.hydrateEntity(party, propertyMap);
      return of(party); // Returning the un-hydrated party for now
    }),
    catchError(error => {
      console.error('Error fetching Party object:', error);
      return of(null);
    })
  );
}

  /**
   * Private function to fetch a single page of parties from the API.
   * This is used by the pagination service.
   * @param lastEvaluatedKey The key for the next page of results.
   */
  private getPageOfParties(lastEvaluatedKey: any = null): Observable<any> {
    const url = environment.URL_PARTIES_GET_PAGE;
    let params: any = {};
    if (lastEvaluatedKey) {
      params.startKey = encodeURIComponent(JSON.stringify(lastEvaluatedKey));
    }

    return this.http.get(url, { headers: { 'Content-Type': 'application/json' }, params }).pipe(
      map((response: any) => ({
        items: response.items || [],
        lastEvaluatedKey: response.lastEvaluatedKey || null
      })),
      catchError(error => {
        console.error('Error fetching party page:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Parties written this session that the server's paginated scan may not have
   * returned yet — either because it's eventually consistent, or because the
   * record sits on a page the stream hasn't reached. Merged into every emission
   * so a freshly-saved party can't be erased by an in-flight refresh, and dropped
   * as soon as the server's own copy shows up.
   */
  private pendingParties = new Map<string, Party>();

  /** Put a just-written party into the live cache immediately. */
  private upsertPartyInCache(party: Party): void {
    if (!party?.vendorNum) return;
    this.pendingParties.set(party.vendorNum, party);
    this.allPartiesSubject.next(this.mergePendingParties(this.allPartiesSubject.getValue()));
  }

  /**
   * Overlay the pending writes on a server list. Once the server list carries a
   * record, its copy wins and the pending entry is retired — so this self-cleans
   * and can't drift.
   */
  private mergePendingParties(serverList: Party[]): Party[] {
    if (!this.pendingParties.size) return serverList;

    const byNum = new Map<string, Party>();
    serverList.forEach(p => { if (p?.vendorNum) byNum.set(p.vendorNum, p); });

    for (const [vendorNum, pending] of Array.from(this.pendingParties.entries())) {
      const fromServer = byNum.get(vendorNum);
      if (fromServer) {
        // Server has it — reconciled, stop tracking it.
        byNum.set(vendorNum, { ...fromServer, ...pending });
        this.pendingParties.delete(vendorNum);
      } else {
        // Not in the server's list yet — keep showing ours.
        const parentName = pending.parentVendor ? byNum.get(pending.parentVendor)?.name : undefined;
        byNum.set(vendorNum, parentName
          ? { ...pending, parentVendorFriendlyName: parentName }
          : pending);
      }
    }

    // Preserve any server rows that had no vendorNum rather than dropping them.
    const keyed = new Set(Array.from(byNum.values()));
    return [...Array.from(byNum.values()), ...serverList.filter(p => !p?.vendorNum && !keyed.has(p))];
  }

  /**
   * Refreshes all parties by calling the paginated API and updating the subject.
   */
  public refreshAllPartiesRecords(): Observable<Party[]> {
    return defer(() => {
      this.crawlsInFlightSubject.next(this.crawlsInFlightSubject.value + 1);
      return this.paginationService.getAllPaginatedDataProgressively(key => this.getPageOfParties(key));
    })
      .pipe(
        finalize(() => this.crawlsInFlightSubject.next(Math.max(0, this.crawlsInFlightSubject.value - 1))),
        map(allPartiesRaw => allPartiesRaw.filter((party: any) => party.vendorData).map((party: any) => party.vendorData)),
        // Hydrate parentVendorFriendlyName for each party that has a parentVendor
        map((partyList: Party[]) => {
          // Build a map of vendorNum -> name for quick lookup
          const partyNameMap = new Map<string, string>();
          partyList.forEach(party => {
            if (party.vendorNum && party.name) {
              partyNameMap.set(party.vendorNum, party.name);
            }
          });
          // Set parentVendorFriendlyName for parties with a parentVendor
          return partyList.map(party => {
            if (party.parentVendor) {
              party.parentVendorFriendlyName = partyNameMap.get(party.parentVendor) || undefined;
            }
            return party;
          });
        }),
        // tap(partyList => console.log('Successfully fetched all parties:', partyList)),
        tap(partyList => this.allPartiesSubject.next(this.mergePendingParties(partyList))),
        catchError(error => {
          console.error('Error fetching all parties:', error);
          // Keep just-written parties even when the refresh fails — otherwise a
          // transient error silently erases the record the user just created.
          this.allPartiesSubject.next(this.mergePendingParties([]));
          return throwError(() => error);
        })
      );
  }

  /**
   * Gets a specific party object from the in-memory cache based on its ID or name.
   * @param partyIdOrName The unique ID or name of the party.
   * @returns An observable that emits the party object.
   */
  public getPartyObject(partyIdOrName: string): Observable<Party | null> {
    // The cache fills ~100 parties at a time (~45 pages for cbhi). Reading it once
    // returned null for any party not loaded YET — every party link and email link
    // opened an empty profile. Emit as soon as the party is in the cache; return
    // null only once no crawl is running, i.e. it really doesn't exist.
    return combineLatest([this.allParties$, this.crawlsInFlightSubject]).pipe(
      map(([parties, crawling]) => {
        const foundParty = parties.find(party => party.vendorNum === partyIdOrName || party.name === partyIdOrName);
        if (foundParty) return foundParty;
        return crawling > 0 ? undefined : null;
      }),
      filter((p): p is Party | null => p !== undefined),
      take(1)
    );
  }

  /**
   * Gets a specific party friendly name from the in-memory cache based on its ID.
   * @param partyId The unique ID of the party.
   * @returns An observable that emits the party's friendly name.
   */
  public getPartyFriendlyName(partyId: string): Observable<string | null> {
    return this.allParties$.pipe(
      map(parties => {
        // Find the party and then safely access the name property.
        const foundParty = parties.find(party => party.vendorNum === partyId);
        return foundParty?.name ?? null;
      }),
      take(1)
    );
  }

  /**
   * Fetches party NMC data from a service.
   * @param partyId The ID of the party.
   * @returns An observable that emits the NMC data.
   */
  public getPartyNmc(partyId: string): Observable<any | null> {
    const options = {
      headers: { 'Content-Type': 'application/json' }
    };
    return this.http.get(`${environment.partyGetNmcUrl}${partyId}`, options).pipe(
      catchError(error => {
        console.error('Error fetching party NMC:', error);
        return of(null);
      })
    );
  }

  /**
   * Writes NMC data to the database.
   * @param nmcObject The NMC data to write.
   * @returns An observable that emits the database response.
   */
  public writeNmc(nmcObject: any): Observable<any> {
    const options = {
      headers: { 'Content-Type': 'application/json' }
    };

    const jsonReadyBody = {
      'partyId': nmcObject.partyId,
      'partyName': nmcObject.partyName,
      'type': 'nmc',
      'year': nmcObject.year,
      'creator': nmcObject.creator,
      'creationDate': nmcObject.creationDate,
      'dateReceived': nmcObject.dateReceived,
      'value': nmcObject.value,
      'description': nmcObject.description,
      'comments': nmcObject.comments,
    };

    return this.http.put(environment.partyWriteDataTableUrl, jsonReadyBody, options).pipe(
      catchError(error => {
        console.error('Error writing NMC:', error);
        return throwError(() => new Error('Could not write NMC data.'));
      })
    );
  }

/**
   * Fetches NMC info from a Google Sheet.
   * @returns An observable that emits an array of NMC info objects.
   */
  public getNmcInfoFromLiveSheet(): Observable<any[]> {
    return this.getOrgNmcSheetSpecifications().pipe(
      map(sheetsResponse => {
        if (!sheetsResponse || !sheetsResponse.values) {
          console.error('PartyService | getNmcInfoFromLiveSheet | sheets came back without values');
          return [];
        }
        const values = sheetsResponse.values;
        // Fix: Explicitly type the array. You can also define an interface for better type safety.
        const returnArray: { year: any; limit: any; registered: any; fields: any; }[] = [];
        returnArray.push({
          year: values[1][1],
          limit: values[1][3],
          registered: values[1][5],
          fields: values[0]
        });
        returnArray.push({
          year: values[3][1],
          limit: values[3][3],
          registered: values[3][5],
          fields: values[2]
        });
        return returnArray;
      }),
      catchError(error => {
        console.error('Error fetching NMC info from live sheet:', error);
        return of([]);
      })
    );
  }

  /**
   * Fetches NMC sheet specifications from a live Google Sheet.
   * @returns An observable of the sheet data.
   */
  private getOrgNmcSheetSpecifications(): Observable<any> {
    const tabName = 'nmc';
    const startCell = 'A2';
    const endCell = 'D7';

    const provisionUrl = `https://sheets.googleapis.com/v4/spreadsheets/${this.googleSheetId}/values/${tabName}!${startCell}:${endCell}`;

    return this.http.get(provisionUrl, this.httpOptions).pipe(
      catchError(error => {
        console.error('party.service | getOrgNmcSheetSpecifications | this will fail if sheet has no nmc tab');
        console.error('An error occurred:', error);
        return of(null);
      })
    );
  }
}